import { useMemo } from 'react';
import type { FC } from 'react';
import { Box } from '@chakra-ui/react';
import { AnimatePresence, motion } from 'framer-motion';
import parse from 'html-react-parser';
import { usePDP } from '../../contexts/pdp/usePDP';
import { Text } from '../../ui-components';
import { ResponsiveImage } from '../responsive-image';
import { AddToBagButton } from './add-to-bag-button/AddToBagButton';
import type { Product } from '../../bff-api/products/model';

interface AddToBagSectionProps {
    product: Product;
}

const AddToBagSection: FC<AddToBagSectionProps> = ({ product }) => {
    const productName = useMemo(
        () => (product?.name ? String(parse(product.name)) : undefined),
        [product],
    );

    return (
        <div
            className="m-4 grid grid-cols-[max-content_auto_max-content] items-center gap-1"
            data-testid="pdp-sticky-add-to-bag-section"
        >
            <div>
                {product.c_imageUrl ? (
                    <ResponsiveImage
                        id={`pdp-sticky-add-to-bag-section-product-${product.id}-image`}
                        src={product.c_imageUrl}
                        alt={productName}
                        width="40px"
                        height="40px"
                    />
                ) : null}
            </div>
            <div>
                <Box hideBelow="sm">
                    <Text>{productName}</Text>
                </Box>
            </div>
            <AddToBagButton includePrice />
        </div>
    );
};

export const StickyAddToBagSection: FC = () => {
    const { product, shouldShowStickyBuyBoxAddToBagButton } = usePDP();

    if (!product) {
        return null;
    }

    return (
        <AnimatePresence>
            {shouldShowStickyBuyBoxAddToBagButton ? (
                <>
                    <motion.div
                        initial={{
                            opacity: 0,
                        }}
                        animate={{ opacity: 1 }}
                        exit={{ opacity: 0 }}
                        transition={{
                            duration: 0.075,
                        }}
                        className="fixed inset-x-0 bottom-0 z-20 bg-white shadow-[0_2px_10px_0_rgba(0,0,0.25)]"
                    >
                        <AddToBagSection product={product} />
                    </motion.div>
                    {/* Dummy to account for the sticky section and not hide the last part of the footer */}
                    <div className="h-18 shrink-0" />
                </>
            ) : null}
        </AnimatePresence>
    );
};
